import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ROLES } from "@/lib/roles";
import { Download, LogOut, Loader2, ShieldCheck, RefreshCcw, Sprout, BadgeIndianRupee } from "lucide-react";
import { Link } from "react-router-dom";
import SubsidyAdmin from "@/components/SubsidyAdmin";

const API = `/api`;
const STORAGE_KEY = "bz_admin_pwd";

function toCSV(rows) {
  if (!rows.length) return "";
  const cols = ["id", "role", "name", "phone", "location", "email", "notes", "created_at", "fields"];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  return [cols.join(","), ...rows.map((r) => cols.map((c) => escape(r[c])).join(","))].join("\n");
}
function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Admin() {
  const [password, setPassword] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(ROLES[0].key);
  const [dataByRole, setDataByRole] = useState({});
  const [stats, setStats] = useState({ total: 0, by_role: {} });
  const [refreshing, setRefreshing] = useState(false);

  const login = useCallback(async (pwd) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password: pwd });
      localStorage.setItem(STORAGE_KEY, pwd);
      setAuthed(true);
      toast.success("Welcome, admin");
    } catch (e) {
      toast.error("Invalid password");
      localStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
    } finally { setLoading(false); }
  }, []);

  const fetchAll = useCallback(async () => {
    if (!password) return;
    setRefreshing(true);
    try {
      const [subsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { headers: { "X-Admin-Password": password } }),
        axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Password": password } }),
      ]);
      const grouped = {};
      ROLES.forEach((r) => (grouped[r.key] = []));
      subsRes.data.forEach((s) => { (grouped[s.role] = grouped[s.role] || []).push(s); });
      setDataByRole(grouped);
      setStats(statsRes.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired, login again");
        localStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
      } else { toast.error("Failed to load submissions"); }
    } finally { setRefreshing(false); }
  }, [password]);

  useEffect(() => { if (password && !authed) { login(password); } }, []); // eslint-disable-line
  useEffect(() => { if (authed) fetchAll(); }, [authed, fetchAll]);

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPassword(""); setAuthed(false);
  };

  const exportCSV = (role) => {
    const rows = dataByRole[role] || [];
    if (!rows.length) return toast.error("No data to export");
    download(`biomass-zone-${role.toLowerCase()}.csv`, toCSV(rows));
    toast.success(`Exported ${rows.length} ${role} submissions`);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-emerald-50 to-white">
        <form
          onSubmit={(e) => { e.preventDefault(); login(password); }}
          className="w-full max-w-md bz-card p-8 bz-reveal"
          data-testid="admin-login-form"
        >
          <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-5">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="text-xs tracking-widest uppercase text-emerald-700 font-semibold">Restricted</div>
          <h1 className="font-display text-2xl mt-1 text-slate-900">Admin access</h1>
          <p className="text-sm text-slate-500 mt-2">Enter your admin password to view all submissions.</p>

          <label className="bz-label mt-6" htmlFor="admin-password">Password</label>
          <input
            id="admin-password" data-testid="admin-password-input" type="password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="bz-input" autoFocus
          />
          <Button type="submit" disabled={loading} data-testid="admin-login-btn"
            className="bz-btn-primary w-full mt-5 rounded-xl h-11 text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter"}
          </Button>
          <Link to="/" className="block mt-4 text-center text-xs text-slate-500 hover:text-emerald-700" data-testid="admin-back-home">
            ← Back to home
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bz-bg)]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center">
              <Sprout className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-xs tracking-widest uppercase text-emerald-700 font-semibold">Admin</div>
              <div className="font-display text-lg text-slate-900">Biomass Zone dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Button onClick={fetchAll} variant="ghost" size="sm" disabled={refreshing}
              data-testid="admin-refresh-btn" className="text-slate-600 hover:text-emerald-700">
              <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={logout} variant="ghost" size="sm" data-testid="admin-logout-btn"
              className="text-slate-600 hover:text-red-600">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
          <StatCard label="Total Roles" value={stats.total} highlight />
          {ROLES.map((r) => (
            <StatCard key={r.key} label={r.title} value={stats.by_role?.[r.key] ?? 0}
              testId={`stat-${r.key.toLowerCase()}`} />
          ))}
          <StatCard label="Subsidy" value={stats.subsidy_total ?? 0}
            testId="stat-subsidy" accent="amber" />
        </div>

        <Tabs defaultValue="roles" className="w-full" data-testid="admin-top-tabs">
          <TabsList className="bg-white border border-slate-200 mb-6">
            <TabsTrigger value="roles" data-testid="top-tab-roles"
              className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              Role Submissions
            </TabsTrigger>
            <TabsTrigger value="subsidy" data-testid="top-tab-subsidy"
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <BadgeIndianRupee className="w-4 h-4 mr-1.5" /> Govt. Subsidy
              <span className="ml-2 text-xs opacity-80">{stats.subsidy_total ?? 0}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="roles">
            <Tabs value={tab} onValueChange={setTab} data-testid="admin-tabs">
              <TabsList className="bg-white border border-slate-200 flex flex-wrap h-auto p-1">
                {ROLES.map((r) => (
                  <TabsTrigger key={r.key} value={r.key} data-testid={`tab-${r.key.toLowerCase()}`}
                    className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-700">
                    {r.title}
                    <span className="ml-2 text-xs opacity-80">{(dataByRole[r.key] || []).length}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {ROLES.map((r) => (
                <TabsContent key={r.key} value={r.key} className="mt-6">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                      <div className="text-xs tracking-widest uppercase text-emerald-700 font-semibold">{r.tagline}</div>
                      <h2 className="font-display text-2xl mt-1 text-slate-900">{r.title} submissions</h2>
                    </div>
                    <Button onClick={() => exportCSV(r.key)} data-testid={`export-csv-${r.key.toLowerCase()}`}
                      className="bz-btn-primary rounded-full text-white">
                      <Download className="w-4 h-4 mr-2" /> Export CSV
                    </Button>
                  </div>

                  <div className="bz-card overflow-hidden">
                    <SubmissionsTable rows={dataByRole[r.key] || []} role={r} />
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </TabsContent>

          <TabsContent value="subsidy">
            <SubsidyAdmin password={password} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ label, value, highlight, testId, accent }) {
  const bg = accent === "amber"
    ? "bg-amber-50 border-amber-200"
    : highlight ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200";
  const txt = accent === "amber"
    ? "text-amber-800"
    : highlight ? "text-emerald-800" : "text-slate-900";
  return (
    <div data-testid={testId}
      className={`rounded-xl p-4 border ${bg}`}>
      <div className="text-xs text-slate-500 uppercase tracking-widest">{label}</div>
      <div className={`font-display text-2xl mt-1 ${txt}`}>{value}</div>
    </div>
  );
}

function SubmissionsTable({ rows, role }) {
  const extraKeys = role.fields.map((f) => f.name);
  if (!rows.length) {
    return <div className="p-10 text-center text-slate-500 text-sm" data-testid="empty-state">No submissions yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-200 hover:bg-transparent">
            <TableHead>Name</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Email</TableHead>
            {extraKeys.map((k) => (
              <TableHead key={k} className="capitalize">{k.replace(/_/g, " ")}</TableHead>
            ))}
            <TableHead>Notes</TableHead>
            <TableHead>Received</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="border-slate-100 hover:bg-emerald-50/40">
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell>{r.phone}</TableCell>
              <TableCell>{r.location}</TableCell>
              <TableCell className="text-slate-600">{r.email || "—"}</TableCell>
              {extraKeys.map((k) => (
                <TableCell key={k} className="text-slate-600">{(r.fields || {})[k] || "—"}</TableCell>
              ))}
              <TableCell className="text-slate-500 max-w-[240px] truncate">{r.notes || "—"}</TableCell>
              <TableCell className="text-slate-500 text-xs">{new Date(r.created_at).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
